/**
 * RunwayPlayer Component
 * Runway videoları için özelleştirilmiş video oynatıcı
 */

import React, { useRef, useState, useEffect } from 'react';
import './RunwayPlayer.css';

interface RunwayPlayerProps {
    videoUrl: string;
}

export const RunwayPlayer: React.FC<RunwayPlayerProps> = ({ videoUrl }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const playAttemptRef = useRef<Promise<void> | null>(null);

    // Auto-play when loaded
    useEffect(() => {
        if (isLoaded && videoRef.current && !isPlaying) {
            // Cancel any pending play attempt
            if (playAttemptRef.current) {
                return;
            }

            const playPromise = videoRef.current.play();
            if (playPromise !== undefined) {
                playAttemptRef.current = playPromise;
                playPromise
                    .then(() => {
                        playAttemptRef.current = null;
                        setIsPlaying(true);
                    })
                    .catch((error) => {
                        playAttemptRef.current = null;
                        // Auto-play might be blocked, user needs to click
                        console.log('Auto-play blocked, waiting for user interaction', error);
                    });
            }
        }
    }, [isLoaded, isPlaying]);

    // Cleanup on unmount or videoUrl change
    useEffect(() => {
        const video = videoRef.current;
        return () => {
            if (video) {
                video.pause();
            }
            playAttemptRef.current = null;
        };
    }, [videoUrl]);

    const handlePlay = async () => {
        if (videoRef.current && !isPlaying) {
            try {
                await videoRef.current.play();
                setIsPlaying(true);
            } catch (error) {
                console.error('Play failed:', error);
            }
        }
    };

    const handlePause = () => {
        if (videoRef.current && isPlaying) {
            // Wait for any pending play promise to resolve
            if (playAttemptRef.current) {
                playAttemptRef.current.then(() => {
                    if (videoRef.current) {
                        videoRef.current.pause();
                        setIsPlaying(false);
                    }
                }).catch(() => {
                    setIsPlaying(false);
                });
            } else {
                videoRef.current.pause();
                setIsPlaying(false);
            }
        }
    };

    const handleLoadedData = () => {
        setIsLoaded(true);
    };

    const handlePlaying = () => {
        setIsPlaying(true);
    };

    const handlePauseEvent = () => {
        setIsPlaying(false);
    };

    return (
        <div className="runway-player">
            <div className="runway-player-container">
                <video
                    ref={videoRef}
                    className="runway-video"
                    src={videoUrl}
                    loop
                    playsInline
                    muted
                    onLoadedData={handleLoadedData}
                    onPlaying={handlePlaying}
                    onPause={handlePauseEvent}
                    onClick={isPlaying ? handlePause : handlePlay}
                />

                {!isPlaying && (
                    <div className="runway-overlay">
                        <button
                            className="runway-play-button"
                            onClick={handlePlay}
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M8 5v14l11-7z" />
                            </svg>
                        </button>
                        <p className="runway-cta">
                            İzlemeye devam et
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};
